import publicPlanFacts from '../config/publicPlanFacts.json';

// Subscription plans and legacy access-period settings
export const TRIAL_DURATION_DAYS = 14; // Legacy access-period duration

const getPublicPlan = (planId: string) => {
	const plan = publicPlanFacts.plans.find(({ id }) => id === planId);
	if (!plan) throw new Error(`Missing public plan facts for ${planId}`);
	return plan;
};

const HOMEOWNER_PUBLIC = getPublicPlan('homeowner');
const HOMEOWNER_PLUS_PUBLIC = getPublicPlan('homeowner_plus');
const PROPERTY_PUBLIC = getPublicPlan('property');
const PORTFOLIO_PUBLIC = getPublicPlan('portfolio');

export const SUBSCRIPTION_PLANS = {
	// ========== CORE SUBSCRIPTION TIERS ==========
	HOMEOWNER: {
		...HOMEOWNER_PUBLIC,
		features: [
			'1 Home',
			'Unlimited Equipment Records',
			'Home Setup Assistant',
			'Lightweight Home Record Check',
			'Manual & Recurring Maintenance Tasks',
			'Notifications & Reminders',
			'Maintenance History Tracking',
			'Basic Warranty Details',
			'Basic Search',
			'1 GB Document Storage',
		],
		permissions: {
			canManageTeam: false,
			canManageTenants: false,
			canViewReports: true,
			canExportData: true,
			canAdvancedAuditTrail: false,
			canManageMultiUnit: false,
			canTrackWarranties: true,
			canLinkParts: true,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: false,
			prioritySupport: false,
			canCreateProperties: true,
			canManageProperties: true,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: false,
			canUseSuggestedMaintenancePackages: true,
			suggestedMaintenancePackageLimit: 999,
			canUseRecurringTasks: true,
			canUseNotifications: true,
		},
	},
	HOMEOWNER_PLUS: {
		...HOMEOWNER_PLUS_PUBLIC,
		features: [
			'Everything in Free, plus:',
			'Up to 5 Homes',
			'Property Groups',
			'Maintley Intelligence',
			'AI Guidance & Knowledge Packs',
			'Advanced Document Processing',
			'10 GB Document Storage',
		],
		permissions: {
			canManageTeam: false,
			canManageTenants: false,
			canViewReports: true,
			canExportData: true,
			canAdvancedAuditTrail: true,
			canManageMultiUnit: false,
			canTrackWarranties: true,
			canLinkParts: true,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: true,
			prioritySupport: false,
			canCreateProperties: true,
			canManageProperties: true,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: false,
			canUseSuggestedMaintenancePackages: true,
			suggestedMaintenancePackageLimit: 999,
			canUseRecurringTasks: true,
			canUseNotifications: true,
		},
	},
	PROPERTY: {
		...PROPERTY_PUBLIC,
		features: [
			'Complete Maintenance Workflow',
			'Up to 7 Properties',
			'Property Reports & Exports',
			'15 GB Document Storage',
			'Simple Team Management',
			'Property Groups',
			'Resident Profiles & Requests',
		],
		permissions: {
			canManageTeam: true,
			canManageTenants: true,
			canViewReports: true,
			canExportData: true,
			canAdvancedAuditTrail: false,
			canManageMultiUnit: false,
			canTrackWarranties: true,
			canLinkParts: true,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: true,
			prioritySupport: false,
			canCreateProperties: true,
			canManageProperties: true,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: true,
			canUseSuggestedMaintenancePackages: true,
			suggestedMaintenancePackageLimit: 999,
			canUseRecurringTasks: true,
			canUseNotifications: true,
		},
	},
	PORTFOLIO: {
		...PORTFOLIO_PUBLIC,
		features: [
			'Everything in Property, plus:',
			'Up to 15 Properties',
			'Full Maintley Intelligence',
			'AI Guidance & Knowledge Packs',
			'Advanced Document Processing',
			'Role-Based Access',
			'Maintenance Assignment Tasks',
			'Cross-property Reporting',
			'Advanced Maintenance Analytics',
			'Property Groups',
			'Expanded Storage & History Retention',
		],
		permissions: {
			canManageTeam: true,
			canManageTenants: true,
			canViewReports: true,
			canExportData: true,
			canAdvancedAuditTrail: true,
			canManageMultiUnit: true,
			canTrackWarranties: true,
			canLinkParts: true,
			canPortfolioReporting: true,
			canAdvancedAnalytics: true,
			canPropertyGroups: true,
			prioritySupport: true,
			canCreateProperties: true,
			canManageProperties: true,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: true,
			canUseSuggestedMaintenancePackages: true,
			suggestedMaintenancePackageLimit: 999,
			canUseRecurringTasks: true,
			canUseNotifications: true,
		},
	},

	// ========== SPECIAL ROLES (Not subscription tiers) ==========
	GUEST: {
		id: 'guest',
		name: 'Guest',
		priceMonthly: 0,
		priceYearly: 0,
		maxProperties: 0, // Cannot create properties, only access shared ones
		maxDevices: 0,
		maxFiles: 0,
		maxStorageGb: 0,
		features: [
			'Access to shared properties only',
			'Cannot create new properties',
			'Limited to properties shared by others',
		],
		permissions: {
			canManageTeam: false,
			canManageTenants: false,
			canViewReports: false,
			canExportData: false,
			canAdvancedAuditTrail: false,
			canManageMultiUnit: false,
			canTrackWarranties: false,
			canLinkParts: false,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: false,
			prioritySupport: false,
			canCreateProperties: false,
			canManageProperties: false,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: false,
			canUseSuggestedMaintenancePackages: false,
			suggestedMaintenancePackageLimit: 0,
			canUseRecurringTasks: false,
			canUseNotifications: false,
		},
	},
	TEAM: {
		id: 'team',
		name: 'Team',
		priceMonthly: 0,
		priceYearly: 0,
		maxProperties: 0, // Uses access granted by the owning account
		maxDevices: 0,
		maxFiles: 0,
		maxStorageGb: 0,
		features: [
			'Access through an invited business account',
			'Cannot create or own subscriptions',
			'Uses the account owner plan limits',
		],
		permissions: {
			canManageTeam: false,
			canManageTenants: false,
			canViewReports: false,
			canExportData: false,
			canAdvancedAuditTrail: false,
			canManageMultiUnit: false,
			canTrackWarranties: false,
			canLinkParts: false,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: false,
			prioritySupport: false,
			canCreateProperties: false,
			canManageProperties: false,
			canSubmitMaintenanceRequests: false,
			canViewTenantInfo: false,
			canUseSuggestedMaintenancePackages: false,
			suggestedMaintenancePackageLimit: 0,
			canUseRecurringTasks: false,
			canUseNotifications: false,
		},
	},
	TENANT: {
		id: 'tenant',
		name: 'Tenant',
		priceMonthly: 0,
		priceYearly: 0,
		maxProperties: 0, // Cannot create properties, only access assigned property
		maxDevices: 0,
		maxFiles: 0,
		maxStorageGb: 0,
		features: [
			'Access to assigned property only',
			'Cannot create or manage properties',
			'Submit maintenance requests',
			'View tenant information (read-only)',
		],
		permissions: {
			canManageTeam: false,
			canManageTenants: false,
			canViewReports: false,
			canExportData: false,
			canAdvancedAuditTrail: false,
			canManageMultiUnit: false,
			canTrackWarranties: false,
			canLinkParts: false,
			canPortfolioReporting: false,
			canAdvancedAnalytics: false,
			canPropertyGroups: false,
			prioritySupport: false,
			canCreateProperties: false,
			canManageProperties: false,
			canSubmitMaintenanceRequests: true,
			canViewTenantInfo: true,
			canUseSuggestedMaintenancePackages: false,
			suggestedMaintenancePackageLimit: 0,
			canUseRecurringTasks: false,
			canUseNotifications: false,
		},
	},
};

export const SUBSCRIPTION_STATUS = {
	ACTIVE: 'active',
	TRIAL: 'trial',
	CANCELLED: 'cancelled',
	EXPIRED: 'expired',
	PAST_DUE: 'past_due',
} as const;

export type SubscriptionStatus =
	(typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS];
